
import React, { useState } from 'react';
import { AdminSidebar } from '@/components/AdminSidebar';
import { Separator } from '@/components/ui/separator';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Edit, Trash2, Plus, Search, ArrowUp, ArrowDown } from 'lucide-react';
import { blogPosts } from '@/data/blogPosts';
import PageTransition from '@/components/PageTransition';
import { Link, useNavigate } from 'react-router-dom';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';

const BlogAdmin = () => {
  const [posts, setPosts] = useState(blogPosts);
  const [searchTerm, setSearchTerm] = useState('');
  const [featuredPosts, setFeaturedPosts] = useState(
    posts.filter(post => post.published).slice(0, 3).map(post => post.id)
  );
  const navigate = useNavigate();
  
  // Filter posts based on search term
  const filteredPosts = posts.filter(post => 
    post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
    post.author.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Delete a post (in a real app, this would call an API)
  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      setPosts(posts.filter(post => post.id !== id));
      // Also remove from featured if it was featured
      if (featuredPosts.includes(id)) {
        setFeaturedPosts(featuredPosts.filter(postId => postId !== id));
      }
      toast.success('Post deleted successfully');
    }
  };

  // Toggle featured status
  const toggleFeatured = (id) => {
    if (featuredPosts.includes(id)) {
      setFeaturedPosts(featuredPosts.filter(postId => postId !== id));
    } else {
      if (featuredPosts.length >= 3) {
        toast.error('You can only feature up to 3 posts');
        return;
      }
      setFeaturedPosts([...featuredPosts, id]);
    }
  };

  // Move featured post up in order
  const moveFeaturedUp = (id) => {
    const index = featuredPosts.indexOf(id);
    if (index > 0) {
      const newFeaturedPosts = [...featuredPosts];
      [newFeaturedPosts[index], newFeaturedPosts[index - 1]] = 
      [newFeaturedPosts[index - 1], newFeaturedPosts[index]];
      setFeaturedPosts(newFeaturedPosts);
    }
  };

  // Move featured post down in order
  const moveFeaturedDown = (id) => {
    const index = featuredPosts.indexOf(id);
    if (index < featuredPosts.length - 1) {
      const newFeaturedPosts = [...featuredPosts];
      [newFeaturedPosts[index], newFeaturedPosts[index + 1]] = 
      [newFeaturedPosts[index + 1], newFeaturedPosts[index]];
      setFeaturedPosts(newFeaturedPosts);
    }
  };
  
  return (
    <div className="flex min-h-screen">
      <AdminSidebar />
      
      <div className="flex-1 p-6 overflow-y-auto">
        <PageTransition>
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">Blog Management</h1>
            <Button onClick={() => navigate('/blog-post-editor')}>
              <Plus className="mr-2 h-4 w-4" />
              New Post
            </Button>
          </div>
          
          <Separator className="mb-6" />
          
          {/* Featured Posts Section */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">Featured Posts</h2>
            <div className="bg-muted p-4 rounded-lg">
              {featuredPosts.length === 0 ? (
                <p className="text-muted-foreground text-center py-4">No featured posts selected. Use the toggle below to feature posts.</p>
              ) : (
                <div className="space-y-3">
                  {featuredPosts.map((id, index) => {
                    const post = posts.find(p => p.id === id);
                    if (!post) return null;
                    
                    return (
                      <div key={id} className="flex items-center justify-between bg-card p-3 rounded-md border">
                        <div className="flex items-center">
                          <span className="font-medium text-primary mr-2">#{index + 1}</span>
                          <span className="font-medium">{post.title}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => moveFeaturedUp(id)}
                            disabled={index === 0}
                          >
                            <ArrowUp className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => moveFeaturedDown(id)}
                            disabled={index === featuredPosts.length - 1}
                          >
                            <ArrowDown className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => toggleFeatured(id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          
          {/* Search and filter */}
          <div className="mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search posts..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
          
          {/* Posts list */}
          <div className="grid grid-cols-1 gap-4">
            {filteredPosts.length === 0 ? (
              <div className="text-center p-8 border rounded-lg bg-gray-50">
                <p className="text-muted-foreground">No posts found</p>
              </div>
            ) : (
              filteredPosts.map(post => (
                <Card key={post.id}>
                  <CardHeader className="p-4 pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        <CardTitle className="text-lg">{post.title}</CardTitle>
                        <CardDescription>Published on {post.date} by {post.author}</CardDescription>
                      </div>
                      <div className="flex space-x-2">
                        <Button variant="outline" size="sm" onClick={() => navigate(`/blog-post-editor/${post.id}`)}>
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit</span>
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDelete(post.id)}>
                          <Trash2 className="h-4 w-4" />
                          <span className="sr-only">Delete</span>
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="p-4 pt-2">
                    <p className="text-sm text-muted-foreground line-clamp-2">{post.excerpt}</p>
                  </CardContent>
                  <CardFooter className="p-4 pt-0 flex items-center justify-between">
                    <div className="flex space-x-2">
                      {post.categories.map(category => (
                        <span 
                          key={category} 
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary"
                        >
                          {category}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <Switch 
                          id={`featured-${post.id}`}
                          checked={featuredPosts.includes(post.id)}
                          onCheckedChange={() => toggleFeatured(post.id)}
                        />
                        <Label htmlFor={`featured-${post.id}`} className="text-sm">
                          Featured
                        </Label>
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {post.published ? 'Published' : 'Draft'}
                      </div>
                    </div>
                  </CardFooter>
                </Card>
              ))
            )}
          </div>
        </PageTransition>
      </div>
    </div>
  );
};

export default BlogAdmin;
